import { Container, Grid, Typography, Button, LinearProgress, Alert, AlertTitle, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BasicUserData from './componets/BasicDataUser';
import { useEffect, useState } from 'react';
import { ContactDetails, dictionaryService, FormDataB2B, Token, sanitizeFormData } from './services/dictionaryService';
import { isValidNIP, isValidREGON, phoneRegex, getIbanError } from './helpers/validation';
import ContactDataB2B from './componets/ContactDataB2B';
import CompanyData from './componets/CompanyData';
import AccoumtB2B from './componets/AccountB2B';
import IbanField from './componets/IbanField';
import { IBAN_PLACEHOLDER } from './helpers/iban';
import RepresentativePerson from './componets/RepresentivePerson';
import AgreementB2B from './componets/AgreementB2B';

export interface FormValuesB2B {
    dataAgreement: boolean;
    personalDataProcessingConsent: boolean;
    privacyPolicyAcknowledgment: boolean;
    firstName: string;
    lastName: string;
    email: string;
    phoneNo: string;
    companyName: string;
    nip: string;
    regon: string;
    legalForm: string;
    representativePersonFirstName: string;
    representativePersonLastName: string;
    representativePersonPosition: string;
    province: string;
    district: string;
    commune: string;
    city: string;
    postalCode: string;
    street: string;
    houseNumber: string;
    apartmentNumber: string | null;
    accountNumber: string;
    bankName: string;
}

const FormPageB2B = ({ tokenAll, contactDetails }: { tokenAll: Token, contactDetails: ContactDetails }) => {

    const [loading, setLoading] = useState(false);
    const [send, setSend] = useState(false);




    const formik = useFormik<FormValuesB2B>({
        initialValues: {
            dataAgreement: false,
            personalDataProcessingConsent: false,
            privacyPolicyAcknowledgment: false,
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            companyName: "",
            nip: "",
            regon: "",
            legalForm: '',
            representativePersonFirstName: "",
            representativePersonLastName: "",
            representativePersonPosition: "",
            province: '',
            district: '',
            commune: '',
            city: '',
            postalCode: '',
            street: '',
            houseNumber: '',
            apartmentNumber: '',
            accountNumber: '',
            bankName: '',

        },
        validationSchema: Yup.object({
            dataAgreement: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            personalDataProcessingConsent: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            privacyPolicyAcknowledgment: Yup.boolean().oneOf([true], 'To pole jest wymagane'),
            firstName: Yup.string().required('Imię jest wymagane'),
            lastName: Yup.string().required('Nazwisko jest wymagane'),
            email: Yup.string().email('Nieprawidłowy email').required('Email jest wymagany'),
            phoneNo: Yup.string()
                .required('Numer telefonu jest wymagany')
                .matches(phoneRegex, 'Niepoprawny numer telefonu (PL)'),
            companyName: Yup.string().required('Nazwa firmy jest wymagana'),
            nip: Yup.string()
                .required('NIP jest wymagany')
                .test('is-valid-nip', 'Nieprawidłowy NIP', value => !value || isValidNIP(value)),
            regon: Yup.string()
                .required('REGON jest wymagany')
                .test('is-valid-regon', 'Nieprawidłowy REGON', value => !value || isValidREGON(value)),
            legalForm: Yup.string().required('Forma prawna jest wymagana'),
            representativePersonFirstName: Yup.string().required('Imię przedstawiciela jest wymagane'),
            representativePersonLastName: Yup.string().required('Nazwisko przedstawiciela jest wymagane'),
            representativePersonPosition: Yup.string().required('Funkcja przedstawiciela jest wymagana'),
            province: Yup.string().required('Województwo jest wymagane'),
            commune: Yup.string().required('Gmina jest wymagana'),
            city: Yup.string().required('Miasto jest wymagane'),
            postalCode: Yup.string()
                .required('Kod pocztowy jest wymagany')
                .matches(/^\d{2}-\d{3}$/, 'Kod pocztowy musi być w formacie 00-000'),
            street: Yup.string().required('Ulica jest wymagana'),
            houseNumber: Yup.string()
                .required('Numer domu jest wymagany')
                .max(6, 'Numer domu nie może być dłuższy niż 6 znaków'),
            apartmentNumber: Yup.string()
                .nullable()
                .max(6, 'Numer mieszkania nie może być dłuższy niż 6 znaków'),
            accountNumber: Yup.string()
                .required('Numer konta jest wymagany')
                // createError, żeby pokazać konkretną przyczynę zamiast jednego ogólnego komunikatu
                .test('is-valid-iban', '', function (value) {
                    const error = getIbanError(value ?? '');
                    return error ? this.createError({ message: error }) : true;
                }),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            if (!tokenAll) return;
            const preparedData: FormDataB2B = {
                formType: "B2B" as const,
                status: "SUBMITTED",
                contactDetails: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    phoneNo: values.phoneNo,
                },
                token: tokenAll,
                name: values.companyName,
                taxId: values.nip,
                statisticalId: values.regon,
                legalForm: values.legalForm,
                registeredOfficeAddress: {
                    country: "Polska",
                    voivodeship: values.province,
                    county: values.district,
                    municipality: values.commune,
                    street: values.street,
                    houseNumber: values.houseNumber,
                    apartmentNumber: values.apartmentNumber || undefined,
                    postalCode: values.postalCode,
                    city: values.city,
                },
                representativePerson: {
                    firstName: values.representativePersonFirstName,
                    lastName: values.representativePersonLastName,
                    position: values.representativePersonPosition,
                },
                settlementData: {
                    accountNumber: values.accountNumber,
                    bankName: values.bankName,
                },
                declarationFooter: {
                    date: new Date().toISOString(),
                },
            };
            try {
                await dictionaryService.postFormB2B(tokenAll.value, sanitizeFormData(preparedData));
                setSend(true);
            } catch {
                alert('Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie.');
            }
            finally {
                setLoading(false);
            }
        },

    });

    useEffect(() => {
        formik.setValues((prevValues) => ({
            ...prevValues,
            firstName: contactDetails.firstName,
            lastName: contactDetails.lastName,
            email: contactDetails.email,
            phoneNo: contactDetails.phoneNo,
            companyName: contactDetails.companyName || prevValues.companyName,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenAll, contactDetails])

    return (
        <>
            <Container maxWidth="lg">
                {send ? (
                    <Alert severity="success" sx={{ marginTop: '160px' }}>
                        <AlertTitle>Formularz został pomyślnie wysłany!</AlertTitle>
                        Dziękujemy za przesłanie formularza.
                    </Alert>
                ) : (
                    <form onSubmit={formik.handleSubmit} style={{ paddingTop: '60px' }}>
                        <Box sx={{
                            mt: 10, mb: 4,
                            borderRadius: '8px',
                            padding: '24px',
                            fontSize: '24px',
                            fontFamily: '"Poppins", sans-serif', // Set font family to Poppins
                            backgroundColor: "#E0F2F1",
                            border: '1px solid #00796B',
                            marginTop: { xs: "120px", mt: "150px" },
                            paddingLeft:
                                { xs: "15px", lg: "24px" },
                            paddingRight: "12px"
                        }}>
                            <Typography variant="h5" gutterBottom>
                                Cześć,
                            </Typography>
                            <Typography variant="body1" gutterBottom >
                                już tylko krok dzieli Cię od dołączenia do grona partnerów{' '}
                                {!window.location.hostname.includes('brandlift') ? 'Sparky' : 'Brandlift'}
                                .  Formalności.
                                <br />
                                <br />
                                Nikt ich nie lubi, ale są konieczne. <span style={{ fontWeight: "600" }}>Poświęć 5 minut </span>i wypełnij uważnie
                                poniższy formularz. Na tej podstawie przygotujemy dokumenty,  które podeślemy Ci do zapoznania się i podpisu. Do dzieła!
                                <br />
                            </Typography>
                        </Box>
                        <BasicUserData<FormValuesB2B> formik={formik} />
                        {/* <BillingDetails formik={formik} /> */}

                        <CompanyData formik={formik} />
                        <RepresentativePerson formik={formik} />

                        <ContactDataB2B formik={formik} />


                        <AccoumtB2B
                            formik={formik}
                            title="Numer konta (IBAN)"
                            name="accountNumber"
                            input={
                                (
                                    <IbanField
                                        sx={{ width: '100%' }}
                                        inlineLabels
                                        value={formik.values.accountNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={IBAN_PLACEHOLDER}
                                        bankName={formik.values.bankName}
                                        setBankName={(v) => formik.setFieldValue('bankName', v)}
                                        setValue={(v) => formik.setFieldValue('accountNumber', v)}
                                        error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
                                        helperText={formik.touched.accountNumber && formik.errors.accountNumber}
                                    />
                                )
                            }
                        />

                        <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '5px', md: '20px' }, marginBottom: { xs: '10px', md: '15px' }}}>
                            Oświadczenie zleceniobiorcy
                        </Typography>
                        <AgreementB2B formik={formik} />
                        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                            <Alert severity="error" sx={{ marginTop: 2 }}>
                                Proszę uzupełnić brakujące lub niepoprawne dane przed wysłaniem formularza.
                            </Alert>
                        )}
                        <Grid container justifyContent="center" sx={{ marginTop: 10, marginBottom: 10, paddingBottom: '200px' }}>
                            {loading ? (
                                <LinearProgress sx={{ width: '100%' }} />
                            ) : (
                                <Button variant="contained" type="submit" color="primary" sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' } }}>
                                    Wyślij
                                </Button>
                            )}
                        </Grid>
                    </form>
                )}
            </Container>

        </>
    );
};

export default FormPageB2B;